import React, { useState, useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useHVAHealthCare } from './HVAHealthCareContext';

interface HVAPatientFormProps {
  onCancel: () => void;
}

/**
 * Component for editing patient data
 */
const HVAPatientForm: React.FC<HVAPatientFormProps> = ({ onCancel }) => {
  const { patientInfo, setPatientInfo, setIsAmkaValidated, addSystemMessage } =
    useHVAHealthCare();

  // Local state for the form
  const [formData, setFormData] = useState({
    fullName: '',
    amka: '',
    phoneNumber: ''
  });

  // Initialize form with existing data
  useEffect(() => {
    if (patientInfo) {
      setFormData({
        fullName: patientInfo.name || '',
        amka: patientInfo.amka || '',
        phoneNumber: patientInfo.phoneNumber || ''
      });
    }
  }, [patientInfo]);

  // Handle field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.fullName || !formData.amka) {
      addSystemMessage('error', 'Name and Health ID are required');
      return;
    }

    // Update patient information
    setPatientInfo({
      voice: patientInfo?.voice || 'eleven_multilingual_v2',
      name: formData.fullName,
      amka: formData.amka,
      phoneNumber: formData.phoneNumber,
      appointmentType: patientInfo?.appointmentType || 'information'
    });

    // Reset AMKA validation
    setIsAmkaValidated(true);

    addSystemMessage('info', 'Patient data updated');
    onCancel();
  };

  return (
    <div className="border rounded mb-3 pt-2">
      <span className="px-2 fs-8 fw-bold">Patient Data</span>
      <div className="p-3 pt-0 pb-2">
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-0">
            <Form.Label>Full Name</Form.Label>
            <Form.Control
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder="E.g. Carlos Martínez"
            />
          </Form.Group>

          <Form.Group className="mb-0">
            <Form.Label>Health ID Number</Form.Label>
            <Form.Control
              type="text"
              name="amka"
              value={formData.amka}
              onChange={handleChange}
              placeholder="E.g. AA12345678"
            />
          </Form.Group>

          <Form.Group className="mb-1">
            <Form.Label>Phone Number</Form.Label>
            <Form.Control
              type="text"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              placeholder="E.g. +34 612 345 678"
            />
          </Form.Group>

          <div className="d-flex justify-content-end mt-3 mb-1">
            <Button
              variant="secondary"
              size="sm"
              onClick={onCancel}
              className="me-2 bg-body-quaternary pt-1 pb-1"
            >
              <FontAwesomeIcon icon={faTimes} className="me-1" />
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              size="sm"
              className="pt-1 pb-1"
            >
              <FontAwesomeIcon icon={faSave} className="me-1" />
              Save Changes
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default HVAPatientForm;
